import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import axios from "axios";
import {
  Button, Grid, CircularProgress, Box, Paper,
  Checkbox, FormControlLabel, Typography, Chip,
  useMediaQuery, useTheme,
} from "@mui/material";
import { PRESENTATION_RESOURCES } from "../talkTreeShared/PresentationGeneration/PresentationResources";
import ExploreMyTalk from '../talkTreeShared/ExploreMyTalk/ExploreMyTalk';
import { useTreeToSvgConverter } from "../talkTreeShared/PresentationGeneration/TreeToSvgConverter";
import SessionTrees, { SortToggle } from "../talkTreeShared/Tree/SessionTrees";
import FlowerTable from "../talkTreeShared/Tree/components/ThreeFlowers";
import { formatTimestampInRegionTimezone, getTimeZoneForRegion } from "../talkTreeShared/Tree/TimezoneUtility";
import { ThemeProvider } from '@mui/material/styles';
import { appTheme, COLORS, FONT_FAMILY, FONT_HEADING } from "../talkTreeShared/Tree/constants/theme";
import { generateFlowerKeyAltText } from "../talkTreeShared/Tree/components/treeAltText";
import Resources from "../components/Resources";
import { API_BASE_URL } from '../constants/constants';
import Tree from "../talkTreeShared/Tree/Tree";
import TreeLibraryFilters from "../talkTreeShared/Tree/Treelibraryfilters";


const PROGRAM_ID_TO_SCHOOL = {
  '66e736f3-a5cc-4803-9645-72f56c43bfde': 'Animas MS',
  'd5c97e3f-642a-49dc-97dc-6592500eead8': 'Christine Duncan',
  'b15ad333-5181-4f27-abbf-7de5ec12b2bd': 'Dennis Chavez ES',
  'bfc56372-79b2-4110-9be7-18793496a0fa': 'Explore Academy - Las Cruces',
  'b5b4232f-fe23-41af-96c5-634b2790b241': 'Explore Academy - Rio Rancho',
  'fd8ad812-92eb-482b-a2d1-001031d0cce1': 'J. Paul Taylor',
  '3820855b-1b23-4517-9ef3-8caa5aaabb32': 'Native American CA',
  '2664ce5f-f33d-46c0-994c-c65741e3b56b': 'NM Academy for the Media Arts',
  'dd5f5742-f151-464b-b89b-2f48f9d2e178': 'Pecos Cyber Academy',
  '0e51372b-f095-45a7-b088-301594a5f70e': 'Polk MS',
  'c28ca31f-ce55-4189-9d7d-ca8561b7ea51': 'Sample School',
  'd167c467-314a-494d-a976-29007fae86fe': 'Lovington Sixth Grade',
  '46de9afb-ec0c-494f-afb6-71ac92f8c067': 'South Valley Preparatory',
  '9305e7ab-9567-4876-a333-2b11dbea60a8': 'Taos Integrated School of the Arts',
  'ffefb256-dbe0-4677-aefb-ed0c681e3830': 'Tatum JHS',
  '6c8bfccf-39ec-40ac-bbdb-1e6b547937f3': 'International School at Mesa',
};

function withSchoolPrefix(programId, title) {
  const school = PROGRAM_ID_TO_SCHOOL[programId];
  if (!school) console.warn(`No school mapping for programId: ${programId}`);
  return `${school || 'school?'}, ${title}`;
}

const TAB_CONTENT_PADDING = '24px';
const LEFT_PANEL_BG = COLORS.page;
const LEFT_PANEL_BORDER = `1px solid ${COLORS.hairline}`;
const LEFT_PANEL_RADIUS = 2;
const EXPLORE_MY_TALK_CLIENTS = ['catalyst', 'nm'];
const PAGE_SOURCE = 'StudentEngagement';

function getCompareImageStyle(screenWidth) {
  if (screenWidth > 1440) return { transform: 'scale(0.8)', transformOrigin: 'center' };
  if (screenWidth > 1024) return { transform: 'scale(0.65)', transformOrigin: 'center' };
  if (screenWidth > 768) return { transform: 'scale(0.5)', transformOrigin: 'center' };
  return { transform: 'scale(0.6)', transformOrigin: 'center' };
}

function useWindowSize() {
  const [size, setSize] = useState({ width: window.innerWidth });
  useEffect(() => {
    const handler = () => setSize({ width: window.innerWidth });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return size;
}


function StudentEngagement() {

  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [transcriptSessionID, setTranscriptSessionID] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [activeVisualization, setActiveVisualization] = useState('transcript');
  const [tutorTalkMoves, setTutorTalkMoves] = useState([]);
  const [studentTalkMoves, setStudentTalkMoves] = useState([]);
  const talkMovesLabels = ['Content Knowledge', 'Learning Community', 'Rigorous Thinking'];
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showGraphs, setShowGraphs] = useState(false);
  const [mathTopicsCovered, setMathTopicsCovered] = useState('');
  const [feedbackSessionHighlights, setFeedbackSessionHighlights] = useState('');
  const [studentParticipation, setStudentParticipation] = useState([]);
  const [analyticstSessionID, setAnalyticsSessionID] = useState('');
  const [tutorName, setTutorName] = useState("");
  const [mostRecentSession, setMostRecentSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const initialSelectionDone = useRef(false);
  const dataFetched = useRef(false);
  const [selectedTutorId, setSelectedTutorId] = useState('');
  const [previousSessionIds, setPreviousSessionIds] = useState([]);
  const [client, setClient] = useState();
  const [analyticsNotFoundError, setAnalyticsNotFoundError] = useState("");
  const [sessionDataCache, setSessionDataCache] = useState({ transcript: null, analytics: null });
  const [dateWasChanged, setDateWasChanged] = useState(false);
  const [selectedConversationStarters, setSelectedConversationStarters] = useState([]);
  const [selectedTreeForCelebrate, setSelectedTreeForCelebrate] = useState(null);
  const [selectedTreesForCompare, setSelectedTreesForCompare] = useState(new Set());
  const [celebrateTreeImage, setCelebrateTreeImage] = useState(null);
  const [compareTreeImages, setCompareTreeImages] = useState([]);
  const [selectedCompareConversationStarters, setSelectedCompareConversationStarters] = useState([]);
  const [selectedFeedbackTree, setSelectedFeedbackTree] = useState(null);
  const [feedbackTreeImage, setFeedbackTreeImage] = useState(null);
  const [mainTabIndex, setMainTabIndex] = useState(0);
  const [leftTree, setLeftTree] = useState(null);
  const [rightTree, setRightTree] = useState(null);
  const [filteredIds, setFilteredIds] = useState(null);
  const [sortOrder, setSortOrder] = useState('descending');
  const toggleSortOrder = () => setSortOrder(o => (o === 'ascending' ? 'descending' : 'ascending'));
  const latestSessionIdRef = useRef(transcriptSessionID);
  const apiCache = new Map();
  const { width: screenWidth } = useWindowSize();
  const [loadStatus, setLoadStatus] = useState(null); // null | 'empty' | 'error'


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    isConverting,
    errors,
    convertTreeToSvg,
    clearSvgData
  } = useTreeToSvgConverter({
    containerSelector: '.tree-container',
    options: { quality: 1, bgcolor: 'transparent', cacheBust: true }
  });


  const calculateSessionDuration = (metrics, sessionDiscourseHighlights) => {
    if (metrics.sessionTime && metrics.sessionTime > 0) return Math.round(metrics.sessionTime / 60);
    if (sessionDiscourseHighlights.length > 0)
      return Math.round(Math.max(...sessionDiscourseHighlights.map(h => h.end)) / 60);
    return 0;
  };

  const getDayKey = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  };

  const formatDateToYYYYMMDD = (date, region) => {
    if (!date) return "";
    try {
      const timezone = getTimeZoneForRegion(region);
      const options = { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' };
      const formattedDate = new Intl.DateTimeFormat('en-US', options)
        .format(date).split('/').map(part => part.padStart(2, '0'));
      return `${formattedDate[2]}-${formattedDate[0]}-${formattedDate[1]}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const PERIOD_PATTERNS = {
    anyInBrackets: { pattern: /\[([a-zA-Z0-9]+)\]/i, valueGroup: 1, displayGroup: 0, priority: 100 },
    classInBrackets: { pattern: /\[(Class\s+(\d+))\]/i, valueGroup: 2, displayGroup: 1, priority: 90 },
    ClassNStar: { pattern: /Class\s+(\d+)/i, valueGroup: 1, displayGroup: 0, priority: 80 },
    periodXX: { pattern: /\(Period\s+([a-zA-Z0-9]+)\)/i, valueGroup: 1, displayGroup: 0, priority: 70 },
    NNN: { pattern: /(\d\d\d)/i, valueGroup: 1, displayGroup: 0, priority: 50 }
  };

  const extractPeriodIdentifier = (sessionTitle) => {
    if (!sessionTitle) return null;
    for (const [key, config] of Object.entries(PERIOD_PATTERNS)) {
      const match = sessionTitle.match(config.pattern);
      if (match) {
        const value = match[config.valueGroup]?.trim();
        const display = config.displayGroup === 0 ? match[0] : match[config.displayGroup];
        if (value) return { type: key, value, display, priority: config.priority };
      }
    }
    return null;
  };

  const extractFeedbackContent = (infoBoxes, panelName) => {
    if (!infoBoxes || infoBoxes.length === 0) return null;
    for (const box of infoBoxes) {
      if (box.boxContent && Array.isArray(box.boxContent)) {
        const panel = box.boxContent.find(content => content.panel === panelName);
        if (panel && panel.infoText) return panel.infoText.join('');
      }
    }
    return null;
  };


  const handleCelebrateTreeSelection = async (sessionId) => {
    console.log('Celebrate tree selection triggered with sessionId:', sessionId);
    if (leftTree === sessionId) {
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
      setSelectedTreesForCompare(new Set(rightTree ? [rightTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === rightTree));
      clearSvgData(sessionId);
    } else {
      setLeftTree(sessionId); setSelectedTreeForCelebrate(sessionId);
      const newCompareSet = new Set(rightTree ? [sessionId, rightTree] : [sessionId]);
      setSelectedTreesForCompare(newCompareSet);
      try {
        await new Promise(resolve => setTimeout(resolve, 200));
        const svgDataUrl = await convertTreeToSvg(sessionId);
        setCelebrateTreeImage(svgDataUrl);
        setCompareTreeImages(prev => {
          const filtered = prev.filter(img => img.sessionId !== sessionId);
          return [{ sessionId, image: svgDataUrl }, ...filtered];
        });
      } catch (error) {
        console.error('Error converting celebrate tree to SVG:', error);
        alert('Failed to capture tree image. Please try again.');
      }
    }
  };

  const handleCompareTreeSelection = async (sessionId) => {
    console.log('Compare tree selection triggered with sessionId:', sessionId);
    if (leftTree === sessionId) {
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
      setSelectedTreesForCompare(new Set(rightTree ? [rightTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === rightTree));
      clearSvgData(sessionId);
      if (selectedFeedbackTree === sessionId) { setSelectedFeedbackTree(null); setFeedbackTreeImage(null); }
    } else if (rightTree === sessionId) {
      setRightTree(null);
      setSelectedTreesForCompare(new Set(leftTree ? [leftTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === leftTree));
      clearSvgData(sessionId);
    } else {
      if (!leftTree) {
        setLeftTree(sessionId); setSelectedTreeForCelebrate(sessionId);
        setSelectedTreesForCompare(new Set([sessionId])); setSelectedFeedbackTree(sessionId);
        try {
          await new Promise(resolve => setTimeout(resolve, 200));
          const svgDataUrl = await convertTreeToSvg(sessionId);
          setCelebrateTreeImage(svgDataUrl); setFeedbackTreeImage(svgDataUrl);
          setCompareTreeImages([{ sessionId, image: svgDataUrl }]);
        } catch (error) { console.error('Error converting tree to SVG:', error); alert('Failed to capture tree image. Please try again.'); }
      } else if (!rightTree) {
        setRightTree(sessionId); setSelectedTreesForCompare(new Set([leftTree, sessionId]));
        try {
          await new Promise(resolve => setTimeout(resolve, 200));
          const svgDataUrl = await convertTreeToSvg(sessionId);
          setCompareTreeImages(prev => [...prev, { sessionId, image: svgDataUrl }]);
        } catch (error) { console.error('Error converting tree to SVG:', error); alert('Failed to capture tree image. Please try again.'); }
      } else {
        const oldRight = rightTree; setRightTree(sessionId);
        setSelectedTreesForCompare(new Set([leftTree, sessionId])); clearSvgData(oldRight);
        try {
          await new Promise(resolve => setTimeout(resolve, 200));
          const svgDataUrl = await convertTreeToSvg(sessionId);
          setCompareTreeImages(prev => {
            const filtered = prev.filter(img => img.sessionId !== oldRight);
            return [...filtered, { sessionId, image: svgDataUrl }];
          });
        } catch (error) { console.error('Error converting tree to SVG:', error); alert('Failed to capture tree image. Please try again.'); }
      }
    }
  };

  const handleFeedbackTreeSelection = async (sessionId) => {
    console.log('Tree selection triggered with sessionId:', sessionId);
    if (!sessionId) { console.warn('⚠️ sessionId was undefined or null'); return; }
    if (selectedFeedbackTree === sessionId) {
      setSelectedFeedbackTree(null); setFeedbackTreeImage(null); clearSvgData(sessionId);
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
    } else {
      setSelectedFeedbackTree(sessionId); setLeftTree(sessionId); setSelectedTreeForCelebrate(sessionId);
      try {
        await new Promise(resolve => setTimeout(resolve, 200));
        const svgDataUrl = await convertTreeToSvg(sessionId);
        setFeedbackTreeImage(svgDataUrl); setCelebrateTreeImage(svgDataUrl);
        setCompareTreeImages(prev => {
          const filtered = prev.filter(img => img.sessionId !== sessionId);
          return [{ sessionId, image: svgDataUrl }, ...filtered];
        });
      } catch (error) { console.error('Error converting feedback tree to SVG:', error); alert('Failed to capture tree image. Please try again.'); }
    }
  };

  const onTreeSelect = (sessionId) => {
    const tabNames = ['Introduction', 'Celebrate', 'Compare', 'Feedback'];
    mixpanel.track("talkTreeSelected", {
      page: 'StudentEngagement',
      sessionId: sessionId,
      currentTab: tabNames[mainTabIndex],
      tabWillSwitchTo: mainTabIndex === 0 ? 'Celebrate' : tabNames[mainTabIndex]
    });

    if (mainTabIndex === 0) { setMainTabIndex(1); handleCelebrateTreeSelection(sessionId); }
    else if (mainTabIndex === 1) handleCelebrateTreeSelection(sessionId);
    else if (mainTabIndex === 2) handleCompareTreeSelection(sessionId);
    else if (mainTabIndex === 3) handleFeedbackTreeSelection(sessionId);
  };


  const handleTabChange = (i) => {
    const tabNames = ['Introduction', 'Celebrate', 'Compare', 'Feedback'];
    mixpanel.track("tabSwitched", {
      page: 'StudentEngagement',
      fromTab: tabNames[mainTabIndex],
      toTab: tabNames[i],
    });
    setMainTabIndex(i);
  };

  const handleTalkmoves = async () => {
    if (transcriptSessionID === "") { setAnalyticsNotFoundError(""); return; }
    if (sessionDataCache.analytics && sessionDataCache.analytics.sessionId === transcriptSessionID)
      return sessionDataCache.analytics.data;
    const currentFetchSessionId = transcriptSessionID;
    setAnalyticsLoading(true); setStudentParticipation([]);
    try {
      const sessionData = data.find(item => item.sessionId === currentFetchSessionId);
      if (!sessionData) throw new Error("Session not found in existing data");
      setAnalyticsNotFoundError(""); setAnalyticsSessionID(sessionData.sessionId);
      const extractCountsInOrder = (data, desiredOrder) =>
        desiredOrder.map(label => { const category = data.find(item => item.label === label); return category ? category.count : 0; });
      if (sessionData.metrics?.tutorTalkmoveCategories) setTutorTalkMoves(extractCountsInOrder(sessionData.metrics.tutorTalkmoveCategories, talkMovesLabels));
      else setTutorTalkMoves([0, 0, 0]);
      if (sessionData.metrics?.studentTalkmoveCategories) setStudentTalkMoves(extractCountsInOrder(sessionData.metrics.studentTalkmoveCategories, talkMovesLabels));
      else setStudentTalkMoves([0, 0, 0]);
      setMathTopicsCovered(sessionData.sessionMetadata?.content?.title || "No topic information available");
      const generateFeedbackHighlights = (metrics) => {
        if (!metrics) return "No feedback data available.";
        const tutorMoveCount = metrics.tutorTalkMoves?.length || 0;
        const studentMoveCount = metrics.studentTalkMoves?.length || 0;
        const participationRatio = metrics.tutorTalkTime && metrics.tutorTalkTime > 0
          ? (metrics.studentTalkTime / metrics.tutorTalkTime).toFixed(2) : "N/A";
        return `This session had ${tutorMoveCount} tutor talk moves and ${studentMoveCount} student talk moves. The ratio of student to tutor participation was ${participationRatio}.`;
      };
      setFeedbackSessionHighlights(generateFeedbackHighlights(sessionData.metrics));
      if (sessionData.participantContributions && Array.isArray(sessionData.participantContributions))
        setStudentParticipation(sessionData.participantContributions.filter(e => e.participantType === "student"));
      else setStudentParticipation([]);
      setSessionDataCache(prev => ({ ...prev, analytics: { sessionId: transcriptSessionID, data: [sessionData] } }));
      setAnalyticsLoading(false);
      return [sessionData];
    } catch (error) {
      console.error("Error fetching talk moves:", error);
      setAnalyticsLoading(false);
      setAnalyticsNotFoundError("The data could not be retrieved. Error: " + error.message);
      return null;
    }
  };

  const extractQueryParams = useCallback(async () => {
    if (dataFetched.current) { console.log("Data already fetched, skipping duplicate call"); return; }
    dataFetched.current = true;
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const tutorId = urlParams.get("tutorId");
    const clientParam = urlParams.get("client");
    const dateRangeLimit = urlParams.get("prior");
    const resolvedClient = clientParam ? clientParam.toLowerCase() : "generic";
    setClient(resolvedClient);
    setIsLoading(true);
    setLoadStatus(null);
    /* global mixpanel */
    mixpanel.identify(tutorId);
    mixpanel.register({ pageSource: PAGE_SOURCE, client: resolvedClient });
    mixpanel.people.set({ client: resolvedClient });
    try {
      var apiURL = `${API_BASE_URL}v1aux/analysis/tutorData?tutorId=${tutorId}&token=${token}`;
      if (dateRangeLimit) apiURL += `&dateRangeLimit=${dateRangeLimit}`;
      const response = await axios.get(apiURL);
      const tutorDataArray = response.data;
      if (!tutorDataArray || tutorDataArray.length === 0) {
        setLoadStatus('empty');
        setIsLoading(false);
        return null;
      }
      const tutor = tutorDataArray[0]?.sessionMetadata.participants.find(p => p.type === "tutor");
      setTutorName(tutor ? tutor.name : "Unknown Tutor");
      const processedData = tutorDataArray.map(session => ({
        sessionId: session.sessionId, tutorId: session.tutorId, tutorName: tutorName,
        region: session.sessionMetadata.region || "", sessionTitle: session.sessionMetadata.title || "Untitled Session",
        startTime: session.sessionMetadata?.startTime || null, lessonSummary: session.sessionMetadata?.lessonSummary || "",
        subject: session.sessionMetadata?.subject || "", grade: session.sessionMetadata?.grade || "",
        endTime: session.sessionMetadata?.endTime || null, metrics: session.metrics || {},
        participants: session.sessionMetadata?.participants || [], participantContributions: session.participantContributions || [],
        sessionMetadata: session.sessionMetadata || {}, sessionDiscourseHighlights: session.sessionDiscourseHighlights || [],
        infoBoxes: session.infoBoxes || [], strengthsOpportunitiesFeedback: session.strengths_opportunities_feedback || []
      }));
      const regionFromData = processedData[0]?.region || processedData[0]?.sessionMetadata?.region || "";
      if (regionFromData) setSelectedRegion(regionFromData);
      var mostRecent = null;
      if (processedData.length > 0) {
        const sortedData = [...processedData].sort((a, b) => b.startTime - a.startTime);
        mostRecent = sortedData[0]; setMostRecentSession(mostRecent);
        setSelectedTutorId(tutorId);
        if (mostRecent.startTime) {
          const sessionDate = new Date(mostRecent.startTime);
          setSelectedDate(new Intl.DateTimeFormat('en-CA').format(sessionDate));
        }
        setAnalyticsSessionID(mostRecent.sessionId); setTranscriptSessionID(mostRecent.sessionId);
      }
      setData(processedData); setIsLoading(false);
      mixpanel.track("tutorFeedbackPageLoaded", {
        tutorId, tutorRegion: regionFromData, sessionCount: processedData.length,
        viewedSessionId: mostRecent ? mostRecent.sessionId : null,
      });

      return { tutorId, tutorName, token, sessions: processedData, mostRecentSession };
    } catch (error) {
      console.error("Error fetching tutor data:", error);
      setLoadStatus(error.response?.status === 404 ? 'empty' : 'error');
      setIsLoading(false); dataFetched.current = false;
      return null;
    }
  }, []);

  const fetchAvailablePeriods = async () => {
    setAvailablePeriods([]);
    try {
      if (!selectedDate || !selectedRegion || !selectedTutorId) { console.log("Missing required data to fetch periods"); return; }
      let sessionsForDate = [];
      if (data && Array.isArray(data)) {
        sessionsForDate = data.filter((session) => {
          const sessionStartTime = session.startTime;
          if (!sessionStartTime) return false;
          const sessionDate = new Date(sessionStartTime);
          return formatDateToYYYYMMDD(sessionDate, selectedRegion) === selectedDate;
        });
      }
      if (sessionsForDate.length > 0) {
        const periods = sessionsForDate.map((session) => {
          const startTime = session.startTime ? new Date(session.startTime) : null;
          if (!startTime || isNaN(startTime.getTime())) { console.error("Invalid session start time:", session.startTime); return null; }
          const sessionTitle = session.sessionTitle || "No Title Available";
          const studentParticipants = (session.sessionMetadata?.participants || []).filter(p => p.type === "student").map(p => ({ id: p.id, name: p.name || p.id }));
          return { id: `${session.sessionId}_${startTime.getTime()}`, label: formatTimestampInRegionTimezone(startTime.getTime(), selectedRegion), sessionId: session.sessionId, startTime: startTime.getTime(), title: sessionTitle, studentDetails: studentParticipants };
        });
        const sortedPeriods = periods.filter(Boolean).sort((a, b) => a.startTime - b.startTime);
        setAvailablePeriods(sortedPeriods);
        if (!dateWasChanged && !initialSelectionDone.current && sortedPeriods.length > 0) setDateWasChanged(false);
        else if (selectedPeriod === "" && !initialSelectionDone.current) {
          const mostRecentPeriod = sortedPeriods[sortedPeriods.length - 1];
          setSelectedPeriod(mostRecentPeriod.id); setTranscriptSessionID(mostRecentPeriod.sessionId);
          setAnalyticsSessionID(mostRecentPeriod.sessionId); setActiveVisualization('talkTree');
          setTimeout(() => { setShowGraphs(true); }, 300);
        }
      } else {
        setSelectedPeriod(""); setTranscriptSessionID(""); setAnalyticsSessionID(""); setShowGraphs(false); setDateWasChanged(false);
      }
    } catch (error) {
      console.error("Error in fetchAvailablePeriods:", error);
      setSelectedPeriod(""); setTranscriptSessionID(""); setAnalyticsSessionID(""); setShowGraphs(false);
    }
  };

  const loadAllSessions = async () => {
    if (!data.length) return;
    setAnalyticsLoading(true);
    try {
      const sortedSessions = [...data].sort((a, b) => a.startTime - b.startTime);
      const mostRecentId = sortedSessions[sortedSessions.length - 1].sessionId;
      const allOtherIds = sortedSessions.map(s => s.sessionId).filter(id => id !== mostRecentId);
      console.log('Loading all sessions:', sortedSessions.length);
      setAnalyticsSessionID(mostRecentId); setTranscriptSessionID(mostRecentId); setSelectedPeriod(mostRecentId);
      setPreviousSessionIds(allOtherIds);
      await processTranscriptData(mostRecentId);
      for (const id of allOtherIds) await processTranscriptData(id);
      setActiveVisualization('talkTree'); setShowGraphs(true);
      await handleTalkmoves(); setAnalyticsLoading(false);
    } catch (error) {
      console.error("Error loading all sessions:", error); setAnalyticsLoading(false);
    }
  };

  const processTranscriptData = useCallback(async (transcriptSessionID) => {
    if (!transcriptSessionID) return null;
    const cacheKey = `${transcriptSessionID}`;
    if (apiCache.has(cacheKey)) return apiCache.get(cacheKey);
    setIsLoading(true);
    try {
      const sessionData = data.find(item => item.sessionId === transcriptSessionID);
      if (!sessionData) throw new Error(`Session ${transcriptSessionID} not found in existing data`);
      const metrics = sessionData.metrics || {};
      const participantContributions = sessionData.participantContributions || [];
      const sessionMetadata = sessionData.sessionMetadata || {};
      const sessionDiscourseHighlights = sessionData.sessionDiscourseHighlights || [];
      const processedData = sessionDiscourseHighlights.map(highlight => ({
        category: highlight.category, end: highlight.end, noticingScore: highlight.noticingScore,
        participantId: highlight.participantId, segmentId: highlight.segmentId, speakerType: highlight.speakerType,
        start: highlight.start, text: highlight.text, type: highlight.type,
        statementId: highlight.segmentId, talkMoveCategory: highlight.category || '', talkMoveType: highlight.type || ''
      }));
      const participants = Array.isArray(sessionMetadata.participants) ? sessionMetadata.participants : [];
      const activeParticipants = participants
        .filter(p => p.type === 'student' && participantContributions.some(c => c.participantId === p.id))
        .map(p => ({ typeof: p.type, id: p.id, name: p.name }));
      const startTime = sessionMetadata.startTime || Date.now();
      const sessionDurationMinutes = calculateSessionDuration(metrics, sessionDiscourseHighlights);
      const realEndTime = startTime + (sessionDurationMinutes * 60 * 1000);
      const sessionMeta = {
        activeParticipants, participants, siteDirectors: sessionMetadata.siteDirectors || [],
        sessionId: sessionMetadata.sessionId || transcriptSessionID, title: withSchoolPrefix(sessionMetadata.org?.programId, sessionMetadata.title || "Untitled Session"),
        tutorId: sessionMetadata.tutorId || '', startTime, endTime: realEndTime, region: sessionMetadata.region || '',
        lessonSummary: sessionMetadata?.lessonSummary || "", subject: sessionMetadata?.subject || "", grade: sessionMetadata?.grade || "",
        feedbackSummary: extractFeedbackContent(sessionData.infoBoxes, 'feedbackSummary') || 'No summary available.',
        feedbackMathTopics: extractFeedbackContent(sessionData.infoBoxes, 'feedbackMathTopics') || 'No math topics available.'
      };
      setSelectedRegion(sessionMeta.region);
      const enrichedData = {
        sessionMetadata: sessionMeta, processedData,
        metrics: {
          tutorMetrics: { talkMoves: metrics.tutorTalkMoves || [], talkMovesCategories: metrics.tutorTalkmoveCategories || [], utteranceCount: metrics.tutorUtteranceCount || 0, talkTime: metrics.tutorTalkTime || 0, mathTermCount: metrics.tutorMathTermCount || 0 },
          studentMetrics: { talkMoves: metrics.studentTalkMoves || [], talkMovesCategories: metrics.studentTalkmoveCategories || [], utteranceCount: metrics.studentUtteranceCount || 0, talkTime: metrics.studentTalkTime || 0, mathTermCount: metrics.studentMathTermCount || 0 },
          sessionMetrics: { sessionTime: metrics.sessionTime || 0, sessionTimeMinutes: sessionDurationMinutes, totalUtterances: metrics.totalUtterances || 0, totalMathTermCount: metrics.totalMathTermCount || 0, longWaitTimes: metrics.longWaitTimes || 0, shortWaitTimes: metrics.shortWaitTimes || 0 }
        }
      };
      apiCache.set(cacheKey, enrichedData); setIsLoading(false);
      return enrichedData;
    } catch (error) {
      console.error("Error processing transcript data:", error);
      setIsLoading(false); return null;
    }
  }, [data]);


  const generateIntroductionPresentation = () => {
    mixpanel.track("presentationGenerated", { page: 'StudentEngagement', presentationType: "Introduction" });
    sessionStorage.setItem('presentationData', JSON.stringify({ mode: 'introduction', tutorName, introContent: PRESENTATION_RESOURCES.tabs.introduction.content, client }));
    window.open('/presentation', '_blank');
  };

  const generateCelebratePresentation = async () => {
    const sessionData = data.find(item => item.sessionId === selectedTreeForCelebrate);
    const scale = screenWidth > 1440 ? 0.8 : screenWidth > 1024 ? 0.65 : screenWidth > 768 ? 0.5 : 0.6;

    mixpanel.track("presentationGenerated", { page: 'StudentEngagement', presentationType: "Celebrate", sessionId: selectedTreeForCelebrate, selectedConversationStarters: selectedConversationStarters });

    sessionStorage.setItem('presentationData', JSON.stringify({
      mode: 'celebrate',
      selectedTree: { sessionId: selectedTreeForCelebrate, startTime: sessionData?.startTime, image: celebrateTreeImage },
      selectedStarters: selectedConversationStarters,
      tutorName,
      client,
      treeScale: scale
    }));
    window.open('/presentation', '_blank');
  };

  const generateComparePresentation = async () => {
    const selectedTreeData = await Promise.all(Array.from(selectedTreesForCompare).map(async (sessionId) => {
      const sessionData = data.find(item => item.sessionId === sessionId);
      return { sessionId, startTime: sessionData?.startTime, image: compareTreeImages.find(img => img.sessionId === sessionId)?.image };
    }));
    const scale = screenWidth > 1440 ? 0.8 : screenWidth > 1024 ? 0.65 : screenWidth > 768 ? 0.5 : 0.6;

    mixpanel.track("presentationGenerated", { page: 'StudentEngagement', presentationType: "Compare", sessionIds: Array.from(selectedTreesForCompare), selectedCompareConversationStarters: selectedCompareConversationStarters });

    sessionStorage.setItem('presentationData', JSON.stringify({
      mode: 'compare',
      selectedTrees: selectedTreeData,
      selectedStarters: selectedCompareConversationStarters,
      tutorName,
      client,
      treeScale: scale
    }));
    window.open('/presentation', '_blank');
  };

  const TabContentWrapper = ({ children }) => (
    <Box sx={{ padding: isMobile ? '16px' : TAB_CONTENT_PADDING, textAlign: 'left' }}>
      {children}
    </Box>
  );

  const LeftPanel = ({ children }) => (
    <Grid item xs={12} md={6} sx={{ border: LEFT_PANEL_BORDER, borderRadius: LEFT_PANEL_RADIUS, backgroundColor: LEFT_PANEL_BG, overflow: 'hidden' }}>
      {children}
    </Grid>
  );

  const RightPanel = ({ children }) => (
    <Grid item xs={12} md={6}>
      <Box sx={{ p: isMobile ? 1 : 2, height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
        {children}
      </Box>
    </Grid>
  );

  const GenerateButton = ({ onClick, disabled, label }) => (
    <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'center' }}>
      <Button
        variant="contained"
        onClick={onClick}
        disabled={disabled}
        sx={{
          backgroundColor: COLORS.primary,
          color: '#FDFBFA',
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '4px',
          fontSize: isMobile ? '13px' : '14px',
          px: 3,
          minHeight: '44px',
          width: isMobile ? '100%' : 'auto',
          '&:hover': { backgroundColor: COLORS.primaryHover },
          '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: '#fff' },
          '&:focus-visible': { outline: `3px solid ${COLORS.primary}`, outlineOffset: '2px' },
        }}
      >
        {label}
      </Button>
    </Box>
  );
  const DeselectButton = ({ onClick, ariaLabel }) => (
    <Box
      component="button"
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        ml: 'auto', flexShrink: 0,
        background: 'none', border: 'none', cursor: 'pointer',
        color: COLORS.textMuted, p: '2px', lineHeight: 0,
        '&:hover': { color: COLORS.error },
        '&:focus-visible': { outline: `2px solid ${COLORS.primary}`, borderRadius: '4px' },
      }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </Box>
  );


  const IntroIcon = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path fillRule="evenodd" clipRule="evenodd" d="M9.88765 23.2351C12.7329 17.8107 10.1066 14.8785 4.96143 13.0935C5.05974 12.7607 5.15835 12.4278 5.25667 12.0949C7.4937 12.1159 9.64471 12.5318 11.4873 14.3565C11.3321 11.3754 12.0655 9.23147 13.4068 7.66003C13.6726 7.78739 13.9381 7.91474 14.204 8.04181C13.4706 9.43927 13.1656 11.0499 13.3771 12.9173C14.0662 11.8396 14.755 11.3112 15.4441 10.8612C15.6704 11.0767 15.8968 11.2918 16.1235 11.5073C12.9535 14.4401 12.7572 18.4597 14.6261 23.2348H9.88736L9.88765 23.2351Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M12.4297 8.25097C10.508 9.90939 8.24434 9.07705 7.29688 6.14767C9.0446 4.9418 12.019 5.17575 12.4297 8.25097Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M14.873 8.8062C16.7951 10.4646 19.0587 9.63256 20.0062 6.70289C18.2585 5.49703 15.284 5.73127 14.873 8.8062Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M16.4863 10.9872C17.0291 12.0598 18.1732 12.1007 19.1204 10.9855C18.5722 10.1429 17.2246 9.71171 16.4863 10.9872Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M16.0881 12.8281C15.9798 14.6477 17.4512 15.5682 19.5493 14.8206C19.4744 13.2983 18.0342 11.7115 16.0881 12.8281Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M19.21 12.3342C20.154 13.2859 21.3801 12.94 22 11.4359C21.1292 10.7295 19.5532 10.7315 19.21 12.3342Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M5.55644 13.9953C5.89427 15.0874 5.13573 15.8652 3.74669 15.7199C3.56463 14.7819 4.20485 13.603 5.55644 13.9953Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M4.61664 12.5131C4.31368 14.2068 2.76002 14.7293 1 13.5842C1.40528 12.2038 3.07755 11.0628 4.61664 12.5131Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M4.92742 10.6515C4.34065 11.5899 3.2883 11.5333 2.51318 10.4327C3.08566 9.70521 4.35694 9.42123 4.92742 10.6515Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M6.41283 8.992C5.13097 10.0455 3.66533 9.46673 3.09229 7.53825C4.25296 6.77755 6.19247 6.97654 6.41283 8.992Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M6.76644 6.51367C4.52026 7.2232 2.90172 5.68957 3.17638 2.85201C5.10732 2.46398 7.55099 3.7483 6.76644 6.51367Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M10.4856 12.4108C9.08912 12.487 8.38802 11.367 8.96907 9.7771C10.1366 9.83907 11.349 10.9375 10.4856 12.4108Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M8.35006 4.93766C7.24626 4.6858 6.95302 3.65817 7.76272 2.55066C8.6573 2.85909 9.35354 3.98366 8.35006 4.93766Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M7.13376 11.6567C5.99966 11.0589 5.98194 9.84874 7.19549 8.87256C8.0835 9.47123 8.51336 10.9056 7.13376 11.6567Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M13.8323 7.24119C12.3635 6.46571 12.3412 4.8974 13.914 3.63354C15.0644 4.40988 15.6206 6.26843 13.8323 7.24119Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M15.4692 5.41166C15.1156 4.34167 15.8487 3.56079 17.2206 3.67819C17.4172 4.59893 16.8088 5.77325 15.4692 5.41166Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path fillRule="evenodd" clipRule="evenodd" d="M10.7503 4.75291C9.20524 3.98027 9.14579 2.34999 10.752 1C11.9659 1.78088 12.5872 3.70083 10.7503 4.75291Z" fill={active ? COLORS.primary : COLORS.textMuted} />
    </svg>
  );

  const CelebrateIcon = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M18.6188 7.71016C18.885 7.44388 18.885 7.01217 18.6188 6.7459C18.3525 6.47962 17.9208 6.47962 17.6545 6.7459C17.3882 7.01217 17.3882 7.44389 17.6545 7.71016C17.9208 7.97643 18.3525 7.97643 18.6188 7.71016Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path d="M20.1239 12.433C20.4887 12.3396 20.7087 11.9681 20.6152 11.6033C20.5218 11.2385 20.1503 11.0186 19.7855 11.112C19.4207 11.2055 19.2008 11.577 19.2942 11.9418C19.3877 12.3065 19.7592 12.5265 20.1239 12.433Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path d="M21.3468 6.34627C21.6131 6.07999 21.6131 5.64828 21.3468 5.38201C21.0805 5.11573 20.6488 5.11573 20.3825 5.382C20.1162 5.64828 20.1162 6.07999 20.3825 6.34627C20.6488 6.61254 21.0805 6.61254 21.3468 6.34627Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path d="M13.0278 15.693C13.2725 16.2774 13.3641 16.7404 13.3641 17.0006C12.7671 16.9889 10.9969 16.4519 9.27309 14.728C7.54924 13.0042 7.01216 11.234 7.00046 10.637C7.37135 10.637 8.15651 10.8246 9.11664 11.3768C8.92949 11.8856 8.70091 12.3769 8.43285 12.8482C8.68093 13.2108 8.98359 13.5919 9.35009 13.9711C10.4784 12.1854 11.0915 10.1175 11.0915 7.90916C11.0915 6.02594 10.6333 4.0243 9.7668 2.36625C9.59232 2.0324 9.18048 1.90325 8.84663 2.07773C8.51278 2.25221 8.38363 2.66404 8.55811 2.99789C9.32329 4.46196 9.72781 6.24526 9.72781 7.90916C9.72781 8.6563 9.64739 9.38347 9.49338 10.0863C8.03418 9.28599 6.71535 9.1042 6.09151 9.72755C5.97941 9.83965 5.88437 10.0019 5.8371 10.1282C5.83661 10.1282 5.47839 11.1877 4.98176 12.6572C5.01831 12.8765 5.0841 13.1158 5.18255 13.3643C5.5861 14.3844 6.42926 15.5205 7.45518 16.5464L6.54623 17.4554C5.63484 16.544 4.85211 15.5478 4.33062 14.5837C4.07768 15.3324 3.81498 16.1092 3.56155 16.8603C3.99385 17.5202 4.55384 18.1903 5.18304 18.8195L4.27409 19.7285C3.83301 19.2874 3.42167 18.8259 3.05565 18.3585C2.46788 20.0935 2.02242 21.4119 2.02242 21.4119C2.00926 21.4548 2 21.4991 2 21.5464C2 21.6721 2.05069 21.7857 2.13305 21.8676C2.21542 21.9499 2.32898 22.0006 2.45423 22.0006C2.50151 22.0006 2.54586 21.9914 2.58875 21.9782C2.58875 21.9782 13.8724 18.166 13.8724 18.164C13.9982 18.1167 14.1605 18.0217 14.2731 17.9096C14.7775 17.4052 14.7546 16.446 14.3047 15.326C13.8666 15.4079 13.4523 15.5283 13.0278 15.6925V15.693Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path d="M13.1092 10.892C13.2423 11.0251 13.4168 11.0918 13.5913 11.0918C13.7657 11.0918 13.9402 11.0251 14.0733 10.892C16.2538 8.71151 17.4552 5.79555 17.4552 2.6822C17.4552 2.30546 17.1501 2.00037 16.7733 2.00037C16.3966 2.00037 16.0915 2.30546 16.0915 2.6822C16.0915 5.43148 15.0324 8.00433 13.1092 9.92751C12.8431 10.1936 12.8431 10.6254 13.1092 10.892Z" fill={active ? COLORS.primary : COLORS.textMuted} />
      <path d="M21.6345 14.2344C19.9759 13.3679 17.9748 12.9097 16.0916 12.9097C14.9838 12.9097 14.047 13.0096 13.1084 13.2718C12.7843 12.8425 12.4143 12.4146 12.0006 12.0008C11.8256 11.8258 11.6482 11.6596 11.4688 11.5002C11.3338 11.9452 11.172 12.3814 10.9873 12.8084C11.0219 12.842 11.0565 12.8756 11.0911 12.9102C11.3675 13.1866 11.6107 13.4605 11.828 13.7295C11.7145 13.7787 11.5999 13.8294 11.4839 13.884C11.1428 14.0439 10.9961 14.4498 11.1559 14.791C11.3158 15.1322 11.7218 15.2789 12.0625 15.119C13.4388 14.4742 14.5588 14.2744 16.0911 14.2744C17.755 14.2744 19.5383 14.6789 21.0028 15.4441C21.1037 15.4967 21.2114 15.5216 21.3182 15.5216C21.5638 15.5216 21.8012 15.3885 21.923 15.1556C22.0975 14.8217 21.9683 14.4099 21.6345 14.2354V14.2344Z" fill={active ? COLORS.primary : COLORS.textMuted} />
    </svg>
  );

  const CompareIcon = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M20.5555 6.31024C20.1194 5.89659 19.413 5.89659 18.9769 6.31024L16 9.13378C15.5639 9.54743 15.5639 10.2175 16 10.6311C16.4361 11.0448 17.1425 11.0448 17.5786 10.6311L18.65 9.61492V14.1176C18.65 15.0909 17.8156 15.8823 16.7895 15.8823H8.07001C7.04388 15.8823 6.20946 15.0909 6.20946 14.1176V9.61492L7.28084 10.6311C7.67707 11.0069 8.43557 11.0332 8.85948 10.6311C9.29559 10.2175 9.29559 9.54743 8.85948 9.13378L5.8823 6.31024C5.44619 5.89659 4.73978 5.89659 4.30367 6.31024L1.32708 9.13378C0.890972 9.54743 0.890972 10.2175 1.32708 10.6311C1.7632 11.0448 2.46961 11.0448 2.90572 10.6311L3.9771 9.61492V14.1176C3.9771 16.2587 5.81294 18 8.07031 18H16.7898C19.0471 18 20.883 16.2587 20.883 14.1176V9.61492L21.9544 10.6311C22.3857 11.0402 23.1019 11.04 23.533 10.6311C23.964 10.2223 23.9691 9.54743 23.533 9.13378L20.5555 6.31024Z" fill={active ? COLORS.primary : COLORS.textMuted} />
    </svg>
  );

  const ExploreIcon = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      {/* Base segment: nearly horizontal, slight upward drift (~4°), shorter */}
      <line x1="4" y1="14" x2="12" y2="13" stroke={active ? COLORS.primary : COLORS.textMuted} strokeWidth="2" strokeLinecap="round" />
      {/* Upper branch: steeper angle (~30° above horizontal), longer */}
      <line x1="12" y1="13" x2="18" y2="8" stroke={active ? COLORS.primary : COLORS.textMuted} strokeWidth="2" strokeLinecap="round" />
      {/* Lower branch: angles down-right (~10° below horizontal) — longer by ~5px */}
      <line x1="12" y1="13" x2="22" y2="16" stroke={active ? COLORS.primary : COLORS.textMuted} strokeWidth="2" strokeLinecap="round" />
      <circle cx="4" cy="14" r="2" fill={active ? COLORS.primary : COLORS.textMuted} />
      <circle cx="12" cy="13" r="2" fill={active ? COLORS.primary : COLORS.textMuted} />
      <circle cx="18" cy="8" r="2" fill={active ? COLORS.primary : COLORS.textMuted} />
      <circle cx="22" cy="16" r="2" fill={active ? COLORS.primary : COLORS.textMuted} />
    </svg>
  );


  const TABS = [
    { label: 'Introduce Talk Trees', Icon: IntroIcon },
    { label: 'Celebrate', Icon: CelebrateIcon },
    { label: 'Compare', Icon: CompareIcon },
    ...(EXPLORE_MY_TALK_CLIENTS.includes(client) ? [{ label: 'Explore My Talk', Icon: ExploreIcon }] : []),
  ];

  const filterSessions = useMemo(
    () => data.map(s => {
      const period = extractPeriodIdentifier(s.sessionTitle);
      const mathTopicRaw = s.sessionMetadata?.content?.title || '';
      const topicList = mathTopicRaw
        ? mathTopicRaw.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      return {
        sessionId: s.sessionId,
        startTime: s.startTime,
        title: withSchoolPrefix(s.sessionMetadata?.org?.programId, period?.display || s.sessionTitle || 'Session'),
        cohort: s.region || null,
        topics: topicList,
        studentGroups: [],
      };
    }),
    [data]
  );

  const { filteredAnalyticsId, filteredPreviousIds } = useMemo(() => {
    const activeIds = filteredIds ?? data.map(s => s.sessionId);
    const activeSet = new Set(activeIds);
    const sortedActive = [...data]
      .filter(s => activeSet.has(s.sessionId))
      .sort((a, b) => a.startTime - b.startTime);
    const fa = sortedActive.length
      ? sortedActive[sortedActive.length - 1].sessionId
      : analyticstSessionID;
    const fp = sortedActive.map(s => s.sessionId).filter(id => id !== fa);
    return { filteredAnalyticsId: fa, filteredPreviousIds: fp };
  }, [data, filteredIds, analyticstSessionID]);

  const renderTreeCard = (sessionId) => {
    const s = data.find(d => d.sessionId === sessionId);
    const title = s?.sessionTitle || '';
    const isSample = title.toLowerCase().startsWith('sample');
    const isSelected = mainTabIndex === 2
      ? (leftTree === sessionId || rightTree === sessionId)
      : leftTree === sessionId;

    return (
      <div
        onClick={() => onTreeSelect(sessionId)}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTreeSelect(sessionId); }
        }}
        style={{
          border: isSelected ? `3px solid ${COLORS.success}` : `1px solid ${COLORS.hairline}`,
          borderRadius: 12,
          background: isSample ? COLORS.infoBg : COLORS.page,
          padding: 10,
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'border-color 0.15s ease',
        }}
      >
        <span style={{
          display: 'inline-block', fontSize: 12, fontWeight: 600,
          background: '#F1EFEC', color: COLORS.text, padding: '3px 8px', borderRadius: 6,
        }}>
          {formatDisplayDate(new Date(s?.startTime))}
        </span>


        <div style={{ height: 210, overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
          <div style={{ transform: 'scale(0.6)', transformOrigin: 'top center' }}>
            <Tree
              processTranscriptData={processTranscriptData}
              sessionId={sessionId}
              maxTreeHeight={219}
              showDescriptionOnly
              client={client}
              isStudentEngagementPage
              region={selectedRegion}
            />
          </div>
        </div>
      </div>
    );
  };
  const renderIntroductionTab = () => (
    <TabContentWrapper>
      <Grid container spacing={2} alignItems="flex-start">
        <LeftPanel>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: isMobile ? '200px' : '100%' }}>
            <Typography sx={{ fontSize: '1.25rem' }} gutterBottom>Sample Talk Tree</Typography>
            <img
              src="/SampleTree.png"
              alt="Sample talk tree showing branches with colored flowers representing student talk moves"
              style={{ maxWidth: '100%', height: 'auto', maxHeight: isMobile ? '250px' : '400px' }}
            />
          </Box>
          <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
        </LeftPanel>

        <RightPanel>
          {PRESENTATION_RESOURCES.tabs.introduction.pages.map((page, i) => (
            <Box key={i} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom> {page.content.heading} </Typography>
              {page.content.description.length > 0 && (
                <Typography variant="body1" paragraph>{page.content.description}</Typography>
              )}
              <Typography variant="body1" component="div">
                <ul style={{ paddingLeft: '1.5em', margin: 0 }}>
                  {page.content.pointList.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              </Typography>
            </Box>
          ))}
          <GenerateButton onClick={generateIntroductionPresentation} label={PRESENTATION_RESOURCES.messages.generatePresentation} />
        </RightPanel>
      </Grid>
    </TabContentWrapper>
  );

  const renderCelebrateTab = () => {
    const handleStarterChange = (starter, checked) =>
      setSelectedConversationStarters(prev => checked ? [...prev, starter] : prev.filter(s => s !== starter));

    return (
      <TabContentWrapper>
        {isConverting && (
          <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.loadingBackground, borderRadius: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} aria-hidden="true" />
            <Typography variant="body2">Converting tree to image...</Typography>
          </Box>
        )}
        {errors.length > 0 && (
          <Box role="alert" sx={{ mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.errorBackground, borderRadius: 1 }}>
            <Typography variant="body2" color="error">Error: {errors[errors.length - 1].error.message}</Typography>
          </Box>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          <LeftPanel>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              p: 2,
            }}>
              {leftTree && celebrateTreeImage ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%', flexWrap: 'nowrap', position: 'relative' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                      {formatDisplayDate(new Date(data.find(item => item.sessionId === leftTree)?.startTime))}
                    </Typography>
                    <Box sx={{ position: 'absolute', right: '20px' }}>
                      <DeselectButton
                        onClick={() => handleCelebrateTreeSelection(leftTree)}
                        ariaLabel="Deselect this tree"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <img
                      src={celebrateTreeImage}
                      alt={`Talk tree for session on ${formatDisplayDate(new Date(data.find(item => item.sessionId === leftTree)?.startTime))}`}
                      style={{
                        display: 'block',
                        objectFit: 'contain',
                        objectPosition: 'top center',
                        maxHeight: '100%',
                        transition: 'transform 0.2s ease',
                        ...getCompareImageStyle(screenWidth),
                      }}
                    />
                  </Box>
                </>
              ) : (
                <Box role="status" aria-label="No tree selected" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', border: LEFT_PANEL_BORDER, borderRadius: 1, p: 2, minHeight: isMobile ? '150px' : '300px', width: '90%' }}>
                  <Typography variant="subtitle1" color="text.secondary">Click a tree in the Library to see it here</Typography>
                </Box>
              )}
            </Box>
            <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
          </LeftPanel>

          <RightPanel>
            <Typography variant="h6" sx={{ fontWeight: '400', fontSize: '1.25rem' }} gutterBottom> {PRESENTATION_RESOURCES.tabs.celebrate.title} </Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>{PRESENTATION_RESOURCES.tabs.celebrate.description}</Typography>
            <Typography variant="h6" sx={{ fontWeight: '400', fontSize: '1.25rem' }} gutterBottom>{PRESENTATION_RESOURCES.tabs.celebrate.conversationStarterTitle}</Typography>
            <Box role="group" aria-label="Conversation starters">
              {PRESENTATION_RESOURCES.tabs.celebrate.conversationStarters.map((starter, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={selectedConversationStarters.includes(starter)}
                      onChange={(e) => handleStarterChange(starter, e.target.checked)}
                      inputProps={{ 'aria-label': starter }}
                      sx={{ '&:focus-visible': { outline: `3px solid ${COLORS.primary}` } }}
                    />
                  }
                  label={starter}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
            <GenerateButton
              onClick={() => { if (!selectedTreeForCelebrate) { alert(PRESENTATION_RESOURCES.messages.selectTree); return; } generateCelebratePresentation(); }}
              disabled={!selectedTreeForCelebrate || isConverting}
              label={PRESENTATION_RESOURCES.messages.generatePresentation}
            />
          </RightPanel>
        </Grid>
      </TabContentWrapper>
    );
  };

  const renderCompareTab = () => {
    const handleStarterChange = (starter, checked) =>
      setSelectedCompareConversationStarters(prev => checked ? [...prev, starter] : prev.filter(s => s !== starter));

    const imageStyle = {
      display: 'block',
      objectFit: 'contain',
      objectPosition: 'center',
      maxHeight: '100%',
      transition: 'transform 0.2s ease',
      ...getCompareImageStyle(screenWidth),
    };

    return (
      <TabContentWrapper>
        {isConverting && (
          <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.loadingBackground, borderRadius: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} aria-hidden="true" />
            <Typography variant="body2">Converting trees to images...</Typography>
          </Box>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          <LeftPanel>
            <Box sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}>
              {[{ key: 'left', tree: leftTree }, { key: 'right', tree: rightTree }].map(({ key, tree }) => (
                <Box
                  key={key}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                  }}
                >
                  {tree && compareTreeImages.find(img => img.sessionId === tree) ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%', flexWrap: 'nowrap', position: 'relative', minHeight: '32px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.65rem' : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                          {formatDisplayDate(new Date(data.find(item => item.sessionId === tree)?.startTime))}
                        </Typography>
                        <Box sx={{ position: 'absolute', right: '20px' }}>
                          <DeselectButton
                            onClick={() => handleCompareTreeSelection(tree)}
                            ariaLabel={`Deselect ${key === 'left' ? 'Selection 1' : 'Selection 2'}`}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <img
                          src={compareTreeImages.find(img => img.sessionId === tree).image}
                          alt={`${key === 'left' ? 'Selection 1' : 'Selection 2'} talk tree for comparison, session on ${formatDisplayDate(new Date(data.find(item => item.sessionId === tree)?.startTime))}`}
                          style={imageStyle}
                        />
                      </Box>
                    </>
                  ) : (
                    <Box role="status" aria-label={key === 'left' ? 'No first tree selected' : 'No second tree selected'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', p: 1, minHeight: isMobile ? '80px' : '200px' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : undefined }}>
                        {key === 'left' ? 'Click a tree in the Library to see it here' : 'Click another tree in the Library to see it here.'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
          </LeftPanel>

          <RightPanel>
            <Typography variant="h6" sx={{ fontWeight: '400', fontSize: '1.25rem' }} gutterBottom> {PRESENTATION_RESOURCES.tabs.compare.title} </Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>{PRESENTATION_RESOURCES.tabs.compare.description}</Typography>
            <Typography variant="h6" sx={{ fontWeight: '400', fontSize: '1.25rem' }} gutterBottom> {PRESENTATION_RESOURCES.tabs.compare.conversationStarterTitle} </Typography>
            <Box role="group" aria-label="Comparison conversation starters">
              {PRESENTATION_RESOURCES.tabs.compare.conversationStarters.map((starter, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={selectedCompareConversationStarters.includes(starter)}
                      onChange={(e) => handleStarterChange(starter, e.target.checked)}
                      inputProps={{ 'aria-label': starter }}
                    />
                  }
                  label={starter}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
            <GenerateButton
              onClick={() => { if (!leftTree || !rightTree) { alert('Please select exactly 2 trees for comparison.'); return; } generateComparePresentation(); }}
              disabled={isConverting || !leftTree || !rightTree}
              label={PRESENTATION_RESOURCES.messages.generatePresentation}
            />
          </RightPanel>
        </Grid>
      </TabContentWrapper>
    );
  };

  useEffect(() => { document.title = "Student Talk Tree Viewer"; }, []);
  useEffect(() => { if (mainTabIndex === 3 && !selectedFeedbackTree && leftTree) handleFeedbackTreeSelection(leftTree); }, [mainTabIndex]);
  useEffect(() => { latestSessionIdRef.current = transcriptSessionID; }, [transcriptSessionID]);
  useEffect(() => { if (data.length > 0 && !initialSelectionDone.current) { initialSelectionDone.current = true; loadAllSessions(); } }, [data]);
  useEffect(() => { extractQueryParams(); }, []);
  useEffect(() => {
    const timeoutId = setTimeout(() => { if (selectedTutorId && selectedDate && selectedRegion) fetchAvailablePeriods(); }, 200);
    return () => clearTimeout(timeoutId);
  }, [selectedTutorId, selectedDate, selectedRegion]);

  return (
    <ThemeProvider theme={appTheme}>
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: FONT_FAMILY }}>
        <div
          style={{ flex: 1, padding: isMobile ? '16px' : '30px', backgroundColor: PRESENTATION_RESOURCES.styles.pageBackground, overflowX: "hidden" }}
          role="main"
        >

          {loadStatus ? (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh', width: '100%', textAlign: 'center', px: 2 }}
              role="status"
              aria-live="polite"
            >
              <Typography variant="h6" sx={{ fontWeight: 500, color: COLORS.text, fontFamily: 'inherit' }}>
                {loadStatus === 'empty' ? 'No trees available' : "We couldn't load your trees"}
              </Typography>
              <Typography sx={{ mt: 1, color: COLORS.textMuted, fontFamily: 'inherit' }}>
                {loadStatus === 'empty'
                  ? 'There are no Talk Trees to show yet. Once a session has been processed, it will appear here.'
                  : 'Please refresh the page or try again later.'}
              </Typography>
            </Box>
          ) : (isLoading || !data.length) ? (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh', width: '100%' }}
              role="status"
              aria-live="polite"
              aria-busy="true"
              aria-label="Loading your tutoring data"
            >
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <style>{`@keyframes tt-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                <path d="M52 28C52 31.1517 51.3792 34.2726 50.1731 37.1844C48.967 40.0962 47.1992 42.742 44.9706 44.9706C42.742 47.1992 40.0962 48.967 37.1844 50.1731C34.2726 51.3792 31.1517 52 28 52C24.8483 52 21.7274 51.3792 18.8156 50.1731C15.9038 48.967 13.258 47.1992 11.0294 44.9706C8.80083 42.742 7.033 40.0962 5.82689 37.1844C4.62078 34.2726 4 31.1517 4 28C4 24.8483 4.62078 21.7274 5.82689 18.8156C7.03301 15.9038 8.80083 13.258 11.0294 11.0294C13.258 8.80083 15.9038 7.033 18.8156 5.82689C21.7274 4.62078 24.8483 4 28 4C31.1517 4 34.2726 4.62078 37.1844 5.82689C40.0962 7.03301 42.742 8.80083 44.9706 11.0294C47.1992 13.258 48.967 15.9038 50.1731 18.8156C51.3792 21.7274 52 24.8483 52 28L52 28Z" stroke="#E7E3DF" strokeWidth="8" />
                <path d="M52 28C52 31.1517 51.3792 34.2726 50.1731 37.1844C48.967 40.0962 47.1992 42.742 44.9706 44.9706C42.742 47.1992 40.0962 48.967 37.1844 50.1731C34.2726 51.3792 31.1517 52 28 52C24.8483 52 21.7274 51.3792 18.8156 50.1731C15.9038 48.967 13.258 47.1992 11.0294 44.9706C8.80083 42.742 7.033 40.0962 5.82689 37.1844C4.62078 34.2726 4 31.1517 4 28" stroke="#4C8EEC" strokeWidth="8" strokeLinecap="round" style={{ transformOrigin: '28px 28px', animation: 'tt-spin 1s linear infinite' }} />
              </svg>
              <Typography variant="h6" sx={{ mt: 3, fontWeight: 500, color: COLORS.text, fontFamily: 'inherit' }}>
                Loading your tutoring data...
              </Typography>
            </Box>
          ) : (
            <>
              {/* ── Page header ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: isMobile ? 0 : '10px' }}>
                <Typography variant="h1" component="h1" sx={{ fontWeight: 700, fontSize: isMobile ? '26px' : '34px', fontFamily: 'inherit' }}>
                  <img
                    src="/TTLogo.svg"
                    alt="Talk Trees"
                    aria-label="Talk Trees home"
                    style={{
                      height: "26px", width: "auto", display: "block",
                      userSelect: "none", flexShrink: 0,
                    }}
                  />
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    const destination = client === 'catalyst' || client === 'teachfx' || client === 'nm' ? 'Playbooks' : 'Resources';
                    mixpanel.track('getStartedClicked', { destination });
                    window.open(
                      destination === 'Playbooks' ? 'https://www.talktrees.ai/playbooks' : './Resources',
                      'talkTreesReference'
                    );
                  }}
                  sx={{
                    backgroundColor: PRESENTATION_RESOURCES.styles.buttonPrimary,
                    color: '#FDFBFA',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: isMobile ? 2 : 3,
                    borderRadius: '4px',
                    minHeight: '40px',
                    fontFamily: 'inherit',
                    '&:hover': { backgroundColor: COLORS.primaryHover },
                  }}
                  aria-label={client === 'catalyst' || client === 'teachfx' || client === 'nm' ? 'View Playbooks' : 'Get Started with Talk Trees Resources'}
                >
                  {client === 'catalyst' || client === 'teachfx' || client === 'nm' ? 'Playbooks' : 'Get Started'}
                </Button>
              </Box>

              {/* ── Main content ── */}
              <Box sx={{ mt: 2 }}>
                {activeVisualization === 'talkTree' && (
                  <>
                    {analyticsLoading ? (
                      <Box
                        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', flexDirection: 'column' }}
                        role="status" aria-live="polite" aria-busy="true"
                      >
                        <CircularProgress size={40} sx={{ mb: 2 }} aria-hidden="true" />
                        <Typography variant="subtitle1" sx={{ fontFamily: 'inherit' }}>Loading analytics data...</Typography>
                      </Box>
                    ) : (
                      <>
                        {analyticsNotFoundError && (
                          <Typography color="error" sx={{ mb: 2, px: 1 }} role="alert">{analyticsNotFoundError}</Typography>
                        )}

                        {selectedPeriod && analyticstSessionID && showGraphs && !analyticsLoading && (
                          <Box sx={{ px: isMobile ? 0 : 1, mb: 4 }}>
                            <div role="region" aria-label="Session Trees Visualization">
                              <TreeLibraryFilters
                                sessions={filterSessions}
                                heading="My Student Talk Trees"
                                actions={<SortToggle sortOrder={sortOrder} onToggle={toggleSortOrder} />}
                                renderTreeCard={renderTreeCard}
                                onFilterChange={(ids) =>
                                  setFilteredIds(prev => {
                                    const next = ids.length === data.length ? null : ids;
                                    const same =
                                      (prev === null && next === null) ||
                                      (Array.isArray(prev) && Array.isArray(next) &&
                                        prev.length === next.length && prev.join('|') === next.join('|'));
                                    return same ? prev : next;
                                  })
                                }
                              >
                                <SessionTrees
                                  processTranscriptData={processTranscriptData}
                                  analyticsSessionID={filteredAnalyticsId}
                                  previousSessionIds={filteredPreviousIds}
                                  isStudentEngagementPage={true}
                                  region={selectedRegion}
                                  key={`${filteredAnalyticsId}-${selectedPeriod}`}
                                  client={client}
                                  label={null}
                                  showHeader={false}
                                  sortOrder={sortOrder}
                                  onToggleSort={toggleSortOrder}
                                  selectedTrees={mainTabIndex === 2 ? new Set([leftTree, rightTree].filter(Boolean)) : new Set([leftTree].filter(Boolean))}
                                  onTreeSelect={onTreeSelect}
                                  selectionMode={mainTabIndex === 1 ? 'single' : mainTabIndex === 2 ? 'compare' : mainTabIndex === 3 ? 'single' : 'none'}
                                  showSimulatedTrees={false}
                                />
                              </TreeLibraryFilters>
                            </div>
                          </Box>
                        )}

                        {selectedPeriod && filteredAnalyticsId && showGraphs && !analyticsLoading && (
                          <Paper elevation={0} sx={{ backgroundColor: 'transparent' }}>

                            {/* ── Tab bar ── */}
                            <Box sx={{ borderBottom: `1px solid ${COLORS.hairline}` }}>
                              <Box sx={{ display: 'flex', alignItems: isMobile ? 'center' : 'flex-end', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1.5 : 0 }}>
                                {!isMobile && (
                                  <Typography sx={{ fontWeight: 400, fontSize: '20px', color: '#111827', pb: 1, fontFamily: FONT_HEADING }}>
                                    What Can I Do With Talk Trees?
                                  </Typography>
                                )}
                                <Box
                                  role="tablist"
                                  aria-label="Talk Tree actions"
                                  sx={{ display: 'flex', width: isMobile ? '100%' : 'auto', overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch' }}
                                >
                                  {TABS.map(({ label, Icon }, i) => {
                                    const active = mainTabIndex === i;
                                    return (
                                      <Box
                                        key={i}
                                        role="tab"
                                        aria-selected={active}
                                        aria-controls={`tabpanel-${i}`}
                                        id={`tab-${i}`}
                                        tabIndex={active ? 0 : -1}
                                        onClick={() => handleTabChange(i)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTabChange(i); }
                                          if (e.key === 'ArrowRight') { e.preventDefault(); handleTabChange((i + 1) % TABS.length); }
                                          if (e.key === 'ArrowLeft') { e.preventDefault(); handleTabChange((i - 1 + TABS.length) % TABS.length); }
                                        }}
                                        sx={{
                                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                                          padding: isMobile ? '8px 12px' : '8px 20px',
                                          cursor: 'pointer',
                                          borderBottom: active ? `2px solid ${COLORS.primary}` : '2px solid transparent',
                                          transition: 'border-color 0.15s ease',
                                          flexShrink: 0,
                                          minWidth: isMobile ? '80px' : 'auto',
                                          '&:hover': { borderBottomColor: active ? COLORS.primary : COLORS.border },
                                          '&:focus-visible': { outline: `3px solid ${COLORS.primary}`, outlineOffset: '-2px', borderRadius: '4px 4px 0 0' },
                                        }}
                                      >
                                        <Icon active={active} />
                                        <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: active ? 600 : 400, color: active ? COLORS.primary : COLORS.textMuted, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                          {label}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Box>

                            {/* ── Tab panels ── */}
                            <Box sx={{ mt: '24px' }}>
                              {TABS.map((_, i) => (
                                <Box key={i} role="tabpanel" id={`tabpanel-${i}`} aria-labelledby={`tab-${i}`} hidden={mainTabIndex !== i}>
                                  {mainTabIndex === i && (
                                    <>
                                      {i === 0 && renderIntroductionTab()}
                                      {i === 1 && renderCelebrateTab()}
                                      {i === 2 && renderCompareTab()}
                                      {i === 3 && EXPLORE_MY_TALK_CLIENTS.includes(client) && (
                                        <ExploreMyTalk
                                          sessionData={data.find(item => item.sessionId === selectedFeedbackTree)}
                                          tutorTalkMoves={tutorTalkMoves}
                                          studentTalkMoves={studentTalkMoves}
                                          mathTopicsCovered={mathTopicsCovered}
                                          feedbackSessionHighlights={feedbackSessionHighlights}
                                          studentParticipation={studentParticipation}
                                          talkMovesLabels={talkMovesLabels}
                                          selectedTreeImage={feedbackTreeImage}
                                          processTranscriptData={processTranscriptData}
                                          selectedTreeId={selectedFeedbackTree}
                                          onTreeSelect={handleFeedbackTreeSelection}
                                          allSessionIds={[selectedFeedbackTree || leftTree, ...previousSessionIds].filter(Boolean)}
                                        />
                                      )}
                                    </>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Paper>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
            </>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}

export default StudentEngagement;